import { File as GeometryTypeFile } from "./data-defs/geometry-types.js";
import { MaterialTypes_T } from "./data-defs/material-types.js";
import { getMaterialFullTypeKey } from "./data-defs/material-full-types.js";

import ThreeGltfGeometryLoader from "./output-assets-three/geometries/gltf-loader.js";
import ThreeCubeGeometryLoader from "./output-assets-three/geometries/cube-loader.js";
import ThreeSphereGeometryLoader from "./output-assets-three/geometries/sphere-loader.js";

import ThreeTextureLoader from "./output-assets-three/textures/texture-loader.js";

//import ThreeExrEnvLightLoader from "./output-assets-three/envlights/exr-loader.js";
import ThreeEnvEnvLightLoader from "./output-assets-three/envlights/env-loader.js";

import VisualPbrBasicMaterialLoader from "./output-assets-three/materials/visual/pbr-basic-loader.js";
import VisualPbrRegularMaterialLoader from "./output-assets-three/materials/visual/pbr-regular-loader.js";
import GLRaycastMaterialLoader from "./output-assets-three/materials/glraycast/loader.js";
import GLRaycastBaseMaterialLoader from "./output-assets-three/materials/glraycast/base-loader.js";
import GLRaycastBaseMaterialPropertyAssigner from "./output-assets-three/materials/glraycast/base-property-assigner.js";
import MaterialMultiLoader from "./output/assets/materials/multi/material-loader.js";

import EmptyObjectCreator from "./output-assets-three/objects/common/empty-creator.js";
import MeshObjectCreator from "./output-assets-three/objects/common/mesh-creator.js";
import GLRaycastMeshObjectCreator from "./output-assets-three/objects/glraycast/mesh-creator.js";
import EmptyObjectMultiCreator from "./output/assets/objects/multi/empty-object-creator.js";
import MeshObjectMultiCreator from "./output/assets/objects/multi/mesh-object-creator.js";

import SkeletonCreator from "./output-assets-three/skeletons/skeleton-creator.js";
import SkeletonMultiCreator from "./output/assets/skeletons/multi/skeleton-creator.js";

import ThreeZbsonAnimationLoader from "./output-assets-three/animations/zbson-animation-loader.js";

import VisualMaterialTransparencyAssigner from "./output-assets-three/materials/visual/transparency-assigner.js";
import VisualMaterialPrimitiveAssigner from "./output-assets-three/materials/visual/primitive-assigner.js";
import VisualMaterialColorAssigner from "./output-assets-three/materials/visual/color-assigner.js";
import VisualMaterialVector2Assigner from "./output-assets-three/materials/visual/vector2-assigner.js";
import VisualMaterialTextureAssigner from "./output-assets-three/materials/visual/texture-assigner.js";
import VisualMaterialEnvLightAssigner from "./output-assets-three/materials/visual/envlight-assigner.js";
import GLRaycastMaterialOwnerAssigner from "./output-assets-three/materials/glraycast/owner-assigner.js";
import GLRaycastMaterialPropertyAssigner from "./output-assets-three/materials/glraycast/property-assigner.js";
import MaterialPropertySplitAssigner from "./output/assets/materials/material-property-split-assigner.js";
import MaterialPropertyMultiAssigner from "./output/assets/materials/multi/material-property-assigner.js";
import MaterialSinglePropertyMultiAssigner from "./output/assets/materials/multi/material-single-property-assigner.js";

import ParentAssigner from "./output-assets-three/objects/common/parent-assigner.js";
import PlacementController from "./output-assets-three/objects/common/placement-controller.js";
import VisibilityController from "./output-assets-three/objects/common/visibility-controller.js";
import MorphTargetController from "./output-assets-three/objects/common/morph-target-controller.js";
import ParentMultiAssigner from "./output/assets/objects/multi/parent-assigner.js";
import PlacementMultiController from "./output/assets/objects/multi/placement-controller.js";
import VisibilityMultiController from "./output/assets/objects/multi/visibility-controller.js";
import MorphTargetMultiController from "./output/assets/objects/multi/morph-target-controller.js";

import SkeletonAssigner from "./output-assets-three/skeletons/skeleton-assigner.js";
import SkeletonMultiAssigner from "./output/assets/skeletons/multi/skeleton-assigner.js";

import ThreeCameraController from "./output-assets-three/camera/camera-controller.js";

import ElementIds from "./glraycaster/element-ids.js";

class OutputAssetsThree {
    constructor({ rootObject, camera, threeFileArrayBufLoader, threeGltfLoader, threeTexLoader }) {
        const matPbrBasicKey = getMaterialFullTypeKey({ type: MaterialTypes_T.PBR, materialType: "basic" });
        const matPbrRegularKey = getMaterialFullTypeKey({ type: MaterialTypes_T.PBR, materialType: "pbr" });

        const matPbrBasicPrimDefaults = {
            "aoMapIntensity": 1,
            "depthTest": true,
            "depthWrite": true,
            "morphNormals": false,
            "morphTargets": false,
            "side": THREE.DoubleSide,
            "skinning": false,
            "wireframe": false,
        };

        const matPbrBasicColDefaults = {
            "color": "0xffffff",
        };

        const matPbrBasicUpdatingPrimProps = ["morphTargets", "skinning"];

        const matPbrRegularPrimDefaults = {
            "aoMapIntensity": 1,
            "bumpScale": 1,
            "depthTest": true,
            "depthWrite": true,
            "emissiveIntensity": 1,
            "envMapIntensity": 1,
            "flatShading": false,
            "metalness": 0,
            "morphNormals": false,
            "morphTargets": false,
            "roughness": 1,
            "side": THREE.DoubleSide,
            "skinning": false,
            "wireframe": false,
        };

        const matPbrRegularColDefaults = {
            "color": "0xffffff",
            "emissive": "0x000000",
        };

        const matPbrRegularVec2Defaults = {
            "normalScale": { x: 1, y: 1 },
        };

        const matPbrRegularUpdatingPrimProps = ["morphTargets", "morphNormals", "skinning", "flatShading"];

        const transpProps = ["transparent", "opacity", "premultipliedAlpha"];

        const visMatPbrBasicPropAssign = new MaterialPropertySplitAssigner(
            {
                primitive: {
                    properties: Object.keys(matPbrBasicPrimDefaults),
                    assigner: new VisualMaterialPrimitiveAssigner(matPbrBasicPrimDefaults, matPbrBasicUpdatingPrimProps),
                },
                color: {
                    properties: Object.keys(matPbrBasicColDefaults),
                    assigner: new VisualMaterialColorAssigner(matPbrBasicColDefaults),
                },
                transparency: {
                    properties: transpProps,
                    assigner: new VisualMaterialTransparencyAssigner(),
                },
            }
        );

        const visMatPbrRegularPropAssign = new MaterialPropertySplitAssigner(
            {
                primitive: {
                    properties: Object.keys(matPbrRegularPrimDefaults),
                    assigner: new VisualMaterialPrimitiveAssigner(matPbrRegularPrimDefaults, matPbrRegularUpdatingPrimProps),
                },
                color: {
                    properties: Object.keys(matPbrRegularColDefaults),
                    assigner: new VisualMaterialColorAssigner(matPbrRegularColDefaults),
                },
                vector2: {
                    properties: Object.keys(matPbrRegularVec2Defaults),
                    assigner: new VisualMaterialVector2Assigner(matPbrRegularVec2Defaults),
                },
                transparency: {
                    properties: transpProps,
                    assigner: new VisualMaterialTransparencyAssigner(),
                },
            }
        );

        const visMaterialControllers = {
            loaders: {
                [matPbrBasicKey]: new VisualPbrBasicMaterialLoader(),
                [matPbrRegularKey]: new VisualPbrRegularMaterialLoader(),
            },

            properties: {
                [matPbrBasicKey]: {
                    generic: visMatPbrBasicPropAssign,
                    texture: new VisualMaterialTextureAssigner(),
                },
                [matPbrRegularKey]: {
                    generic: visMatPbrRegularPropAssign,
                    texture: new VisualMaterialTextureAssigner(),
                    envLight: new VisualMaterialEnvLightAssigner(),
                },
            },
        };

        const glrcMatLoader = new GLRaycastMaterialLoader();
        const glrcMatPropAssigner = new GLRaycastMaterialPropertyAssigner();
        const glrcMatOwnerAssigner = new GLRaycastMaterialOwnerAssigner();

        const glrcMaterialControllers = {
            loaders: {
                [matPbrBasicKey]: new GLRaycastBaseMaterialLoader(glrcMatLoader, glrcMatPropAssigner, glrcMatOwnerAssigner),
                [matPbrRegularKey]: new GLRaycastBaseMaterialLoader(glrcMatLoader, glrcMatPropAssigner, glrcMatOwnerAssigner),
            },

            properties: {
                [matPbrBasicKey]: {
                    generic: new GLRaycastBaseMaterialPropertyAssigner(),
                },
                [matPbrRegularKey]: {
                    generic: new GLRaycastBaseMaterialPropertyAssigner(),
                },
            },
        };

        const materialControllers = {
            loaders: {
                [matPbrBasicKey]: new MaterialMultiLoader({
                    visual: visMaterialControllers.loaders[matPbrBasicKey],
                    glraycast: glrcMaterialControllers.loaders[matPbrBasicKey],
                }),
                [matPbrRegularKey]: new MaterialMultiLoader({
                    visual: visMaterialControllers.loaders[matPbrRegularKey],
                    glraycast: glrcMaterialControllers.loaders[matPbrRegularKey],
                }),
            },

            properties: {
                [matPbrBasicKey]: {
                    generic: new MaterialPropertyMultiAssigner({
                        visual: visMaterialControllers.properties[matPbrBasicKey].generic,
                        glraycast: glrcMaterialControllers.properties[matPbrBasicKey].generic,
                    }),
                    texture: new MaterialPropertyMultiAssigner({
                        visual: visMaterialControllers.properties[matPbrBasicKey].texture,
                    }),
                },
                [matPbrRegularKey]: {
                    generic: new MaterialPropertyMultiAssigner({
                        visual: visMaterialControllers.properties[matPbrRegularKey].generic,
                        glraycast: glrcMaterialControllers.properties[matPbrRegularKey].generic,
                    }),
                    texture: new MaterialPropertyMultiAssigner({
                        visual: visMaterialControllers.properties[matPbrRegularKey].texture,
                    }),
                    envLight: new MaterialSinglePropertyMultiAssigner({
                        visual: visMaterialControllers.properties[matPbrRegularKey].envLight,
                    }),
                },
            },
        };

        const visObjectControllers = {
            loaders: {
                "Object3D": new EmptyObjectCreator(THREE.Object3D),
                "Group": new EmptyObjectCreator(THREE.Group),
                "Mesh": new MeshObjectCreator(THREE.Mesh),
                "Bone": new EmptyObjectCreator(THREE.Bone),
                "SkinnedMesh": new MeshObjectCreator(THREE.SkinnedMesh),
            },

            properties: {
                parentAssigner: new ParentAssigner(rootObject.objects.visual),
                placementController: new PlacementController(),
                visibilityController: new VisibilityController(),
                morphTargetController: new MorphTargetController(),
            },
        };

        const elementIds = new ElementIds();

        const glrcObjectControllers = {
            loaders: {
                "Object3D": new EmptyObjectCreator(THREE.Object3D),
                "Group": new EmptyObjectCreator(THREE.Group),
                "Mesh": new GLRaycastMeshObjectCreator(new MeshObjectCreator(THREE.Mesh), elementIds),
                "Bone": new EmptyObjectCreator(THREE.Bone),
                "SkinnedMesh": new GLRaycastMeshObjectCreator(new MeshObjectCreator(THREE.SkinnedMesh), elementIds),
            },

            properties: {
                parentAssigner: new ParentAssigner(rootObject.objects.glraycast),
                placementController: new PlacementController(),
                visibilityController: new VisibilityController(),
                morphTargetController: new MorphTargetController(),
            },
        };

        const objectControllers = {
            loaders: {
                "Object3D": new EmptyObjectMultiCreator({
                    visual: visObjectControllers.loaders["Object3D"],
                    glraycast: glrcObjectControllers.loaders["Object3D"],
                }),
                "Group": new EmptyObjectMultiCreator({
                    visual: visObjectControllers.loaders["Group"],
                    glraycast: glrcObjectControllers.loaders["Group"],
                }),
                "Mesh": new MeshObjectMultiCreator({
                    visual: visObjectControllers.loaders["Mesh"],
                    glraycast: glrcObjectControllers.loaders["Mesh"],
                }),
                "Bone": new EmptyObjectMultiCreator({
                    visual: visObjectControllers.loaders["Bone"],
                    glraycast: glrcObjectControllers.loaders["Bone"],
                }),
                "SkinnedMesh": new MeshObjectMultiCreator({
                    visual: visObjectControllers.loaders["SkinnedMesh"],
                    glraycast: glrcObjectControllers.loaders["SkinnedMesh"],
                }),
            },

            properties: {
                parentAssigner: new ParentMultiAssigner({
                    visual: visObjectControllers.properties.parentAssigner,
                    glraycast: glrcObjectControllers.properties.parentAssigner,
                }),
                placementController: new PlacementMultiController({
                    visual: visObjectControllers.properties.placementController,
                    glraycast: glrcObjectControllers.properties.placementController,
                }),
                visibilityController: new VisibilityMultiController({
                    visual: visObjectControllers.properties.visibilityController,
                    glraycast: glrcObjectControllers.properties.visibilityController,
                }),
                morphTargetController: new MorphTargetMultiController({
                    visual: visObjectControllers.properties.morphTargetController,
                    glraycast: glrcObjectControllers.properties.morphTargetController,
                }),
            },
        };

        const visSkeletonControllers = {
            loader: new SkeletonCreator(),
            assigner: new SkeletonAssigner(),
        };

        const glrcSkeletonControllers = {
            loader: new SkeletonCreator(),
            assigner: new SkeletonAssigner(),
        };

        const skeletonControllers = {
            loader: new SkeletonMultiCreator({
                visual: visSkeletonControllers.loader,
                glraycast: glrcSkeletonControllers.loader,
            }),
            assigner: new SkeletonMultiAssigner({
                visual: visSkeletonControllers.assigner,
                glraycast: glrcSkeletonControllers.assigner,
            }),
        };

        this._params = {
            geometryLoaders: {
                [GeometryTypeFile]: new ThreeGltfGeometryLoader(threeGltfLoader),
                "Cube": new ThreeCubeGeometryLoader(),
                "Sphere": new ThreeSphereGeometryLoader(),
            },

            textureLoader: new ThreeTextureLoader(threeTexLoader),

            envLightLoaders: {
                //"exr": new ThreeExrEnvLightLoader(),
                "env": new ThreeEnvEnvLightLoader(threeFileArrayBufLoader),
            },

            materialControllers,
            objectControllers,
            skeletonControllers,

            animationLoader: new ThreeZbsonAnimationLoader(threeFileArrayBufLoader),

            cameraController: new ThreeCameraController(camera),

            glraycastElementIds: elementIds,
        };
    }

    get params() {
        return this._params;
    }
}

export default OutputAssetsThree;
